import React, { type ReactNode } from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import { classes } from 'src/modules/shared/utils/styles.util';

/** One labeled value in a description list. */
export interface DescriptionListItem {
  /** Visible label; also the item's key, so it must be unique in the list. */
  label: string;
  /** Value shown next to the label; plain text is rendered with `Text`. */
  value: ReactNode;
}

/** Props for a list of labeled values. */
interface DescriptionListProps {
  /** Labeled values, in display order. */
  items: DescriptionListItem[];
  /** Extra classes for the list. */
  className?: string;
}

/**
 * Label and value pairs, such as a status summary or record details. Labels
 * sit beside their values from the `sm` breakpoint and above them on phones,
 * so long values keep the full width.
 *
 * @param props - Items and optional classes.
 * @returns The description list.
 */
export const DescriptionList: React.FC<DescriptionListProps> = ({
  items,
  className,
}) => (
  <dl
    className={classes(
      'grid grid-cols-1 gap-y-0.5 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center sm:gap-x-6 sm:gap-y-2',
      className
    )}
  >
    {items.map((item) => (
      <React.Fragment key={item.label}>
        <Text as="dt" size="small" color="secondary">
          {item.label}
        </Text>
        <dd className="mb-2 min-w-0 last:mb-0 sm:mb-0">
          {typeof item.value === 'string' ? (
            <Text size="small" className="break-words">
              {item.value}
            </Text>
          ) : (
            item.value
          )}
        </dd>
      </React.Fragment>
    ))}
  </dl>
);
